import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { FlashSaleEyebrow } from '~/features/promotions/components/flash-sale-eyebrow';
import {
  HALLOWEEN_2026_ASSETS,
  HALLOWEEN_2026_COLORS,
  HALLOWEEN_2026_CTA_LABEL,
  HALLOWEEN_2026_EYEBROW_LABEL,
  HALLOWEEN_2026_HEADLINE_PREFIX,
  HALLOWEEN_2026_VARIANTS,
} from '~/features/promotions/constants/halloween-2026';
import type { Halloween2026Variant } from '~/features/promotions/utils/get-halloween-2026-variant';

export function Halloween2026HomepageCard({
  checkoutHref,
  imagePriority,
  variant,
  onClick,
}: {
  checkoutHref: string;
  imagePriority?: boolean;
  variant: Halloween2026Variant;
  onClick: () => void;
}) {
  const copy = HALLOWEEN_2026_VARIANTS[variant];
  const id = `halloween-2026-${variant}-title`;

  return (
    <PromoCard
      backgroundColor='black'
      backgroundImageSlot={<HomepageBackground imagePriority={imagePriority} />}
      id={id}
      padding={{ base: '32px', md: '48px' }}
      sectionGap={{ base: 4, md: 6 }}
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            _active: { backgroundColor: HALLOWEEN_2026_COLORS.ctaHover },
            _hover: { backgroundColor: HALLOWEEN_2026_COLORS.ctaHover },
            backgroundColor: HALLOWEEN_2026_COLORS.cta,
            borderRadius: '4px',
            color: 'white',
            fontSize: '15px',
            fontWeight: '500',
            height: '45px',
            paddingX: '24px',
            position: 'relative',
            width: { base: '100%', md: '152px' },
            zIndex: 1,
            onClick,
          }}
        >
          {HALLOWEEN_2026_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            id={id}
            letterSpacing='-0.01em'
            lineHeight={{ base: 1.15, md: 1.1 }}
            marginBottom={{ base: 4, md: 3 }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              <Box
                as='span'
                color={HALLOWEEN_2026_COLORS.accent}
                display={copy.homepageHeadlineBreaksOnMobile ? { base: 'block', md: 'inline' } : 'inline'}
              >
                {HALLOWEEN_2026_HEADLINE_PREFIX}
              </Box>{' '}
              {copy.headlineOffer}
            </LinkOverlay>
          </Heading>
          <Text
            color={HALLOWEEN_2026_COLORS.subtitle}
            fontSize={{ base: '0.875rem', md: 'md' }}
            letterSpacing={{ base: '-0.01em', md: 'normal' }}
            lineHeight={{ base: 1.4, md: 1.5 }}
            maxWidth={{ base: '196px', md: 'none' }}
          >
            {copy.homepageSubtitle}
          </Text>
        </Box>
      }
      eyebrowSlot={
        <FlashSaleEyebrow
          color={HALLOWEEN_2026_COLORS.eyebrow}
          dividerGradient={HALLOWEEN_2026_COLORS.dividerGradient}
          icon='clock'
          label={HALLOWEEN_2026_EYEBROW_LABEL}
        />
      }
    />
  );
}

function HomepageBackground({ imagePriority }: { imagePriority?: boolean }) {
  return (
    <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0} zIndex={0}>
      <Box display={{ base: 'block', md: 'none' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={HALLOWEEN_2026_ASSETS.homepageBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'right top' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='(max-width: 1120px) 100vw, 1056px'
          src={HALLOWEEN_2026_ASSETS.homepageBg}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          fill
        />
      </Box>
    </Box>
  );
}
